import styled, { css, DefaultTheme } from "styled-components";
import { theme } from "../../../constants/StyledComponents";
import { AvatarDimensionsEnum, AvatarDirectionEnum } from "./Avatar";

type StyledAvatarProps = {
  $variant: AvatarDimensionsEnum;
  $withBorder?: boolean;
  $badgeColor?: string;
};

type StyledAvatarWrapperProps = {
  $direction?: AvatarDirectionEnum;
};

/* variants */

const variantStyles = (theme: DefaultTheme, variant: AvatarDimensionsEnum) =>
  ({
    xx_small: css`
      height: ${theme.variables.height_9};
      width: ${theme.variables.height_9};
    `,
    x_small: css`
      height: ${theme.variables.height_5};
      width: ${theme.variables.height_5};
    `,
    small: css`
      height: ${theme.variables.height_10};
      width: ${theme.variables.height_10};
    `,
    big: css`
      height: ${theme.variables.height_1};
      width: ${theme.variables.height_1};
    `,
    large: css`
      height: ${theme.variables.height_4};
      width: ${theme.variables.height_4};
    `,
  })[variant];

const StyledAvatar = styled.div<StyledAvatarProps>`
  ${({ theme, $variant: variant }) => variantStyles(theme, variant)}
  background-color: ${theme.colors.purple_3};
  position: relative;
  border-radius: 100%;
  box-shadow: 0px 3px 8px 0px rgba(0, 0, 0, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  ${({ $withBorder, $badgeColor }) =>
    $withBorder
      ? css`
          border: 2px solid ${$badgeColor ?? theme.colors.purple};
        `
      : null};
`;

const StyledAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 100%;
`;

const StyledBadgeAvatar = styled.div<StyledAvatarProps>`
  box-shadow: 0px 2px 8px 0px rgba(74, 74, 79, 0.2);
  position: absolute;
  bottom: -5px;
  right: ${({ $variant: variant }) =>
    variant === AvatarDimensionsEnum.BIG ? "-5px" : "-3px"};
  background-color: ${({ $badgeColor }) => $badgeColor ?? theme.colors.purple};
  width: ${({ $variant: variant }) =>
    variant === AvatarDimensionsEnum.BIG
      ? theme.variables.fs_22
      : theme.variables.fs_14};
  height: ${({ $variant: variant }) =>
    variant === AvatarDimensionsEnum.BIG
      ? theme.variables.fs_22
      : theme.variables.fs_14};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;

  & > svg {
    width: ${({ $variant: variant }) =>
      variant === AvatarDimensionsEnum.BIG ? "16px" : "10px"};
    height: ${({ $variant: variant }) =>
      variant === AvatarDimensionsEnum.BIG ? "16px" : "10px"};
    stroke-width: ${({ $variant: variant }) =>
      variant === AvatarDimensionsEnum.BIG
        ? theme.variables.sw_2
        : theme.variables.sw_1};
    stroke: ${theme.colors.white};
  }
`;

/* variants direction*/
const variantDirectionStyles = (direction: AvatarDirectionEnum) =>
  ({
    vertical: css`
      flex-direction: column;
      align-items: center;
      justify-content: center;
      & .avatar-name-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 15px;
      }
    `,
    horizontal: css`
      flex-direction: row;
      align-items: center;
      & > svg {
        flex-shrink: 0;
      }
      & .avatar-name-text {
        display: flex;
        flex-direction: column;
        margin-left: 15px;
        overflow-wrap: anywhere;
        & > span {
          max-height: 86px;
          overflow: hidden;
        }
      }
    `,
  })[direction];

const StyledAvatarLayout = styled.div<StyledAvatarWrapperProps>`
  ${({ $direction = AvatarDirectionEnum.HORIZONTAL }) =>
    variantDirectionStyles($direction)}
  display: flex;
`;

export {
  StyledAvatar,
  StyledAvatarImage,
  StyledBadgeAvatar,
  StyledAvatarLayout,
};
